import React from 'react';

interface StatCardProps {
  /** Material icon name */
  icon?: string;
  /** Main numeric or text value */
  value: React.ReactNode;
  /** Label below the value */
  label: string;
  /** Optional click handler (makes card clickable) */
  onClick?: () => void;
  /** Highlight color for the value (defaults to on-surface) */
  valueColor?: string;
  /** Optional progress bar (0-100) */
  progress?: number;
  /** Optional subtitle under the label */
  subtitle?: string;
}

/**
 * Reusable stat/metric card used on dashboards and project detail pages.
 * Matches the "Anthracite & Ink" design system with bordered icon box,
 * mono label, and display-font value.
 *
 * @example
 * <StatCard icon="auto_stories" value={5} label="Series" onClick={() => navigate('/home/serials')} />
 * <StatCard icon="checklist" value={12} label="Open Tasks" valueColor="text-tertiary" />
 * <StatCard icon="script" value="7/10" label="Script" progress={70} />
 */
const StatCard: React.FC<StatCardProps> = ({
  icon,
  value,
  label,
  onClick,
  valueColor,
  progress,
  subtitle,
}) => {
  return (
    <div
      className={`card p-4 ${onClick ? 'cursor-pointer hover:bg-surface-container transition-colors' : ''}`}
      onClick={onClick}
    >
      {icon && (
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 flex items-center justify-center border border-outline-variant">
            <span className="material-icons text-on-surface-variant text-lg">
              {icon}
            </span>
          </div>
        </div>
      )}
      <p
        className={`text-2xl font-display font-bold ${valueColor || 'text-on-surface'}`}
      >
        {value}
      </p>
      <p className="text-[0.65rem] font-mono text-on-surface-variant/50 uppercase tracking-wider mt-1">
        {label}
      </p>
      {subtitle && (
        <p className="text-xs text-on-surface-variant/30 mt-0.5">{subtitle}</p>
      )}
      {progress !== undefined && (
        <div className="w-full h-1 bg-outline-variant/20 mt-2 overflow-hidden">
          <div
            className="h-full bg-tertiary transition-all duration-300"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default StatCard;